import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import dbservice from "../../Utility/DbConnection/dbservice";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { json } from "react-router-dom";

function LoadProfilesDialog({
  visible,
  onHide,
  setSelectedMeasures,
  profileType,
}) {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const { t } = useTranslation();
  const toast = useRef(null);
  const filters = useRef({
    SelectedMeasureName: { value: null, matchMode: "contains" },
    Machine: { value: null, matchMode: "contains" },
  });
  useEffect(() => {
    console.log("LoadProfilesDialog is visible");
    if (visible) {
      fetchProfiles();
    } else {
      setSelectedProfile(null);
    }
  }, [visible]);

  const fetchProfiles = async () => {
    try {
      const content = {
        ProfileType: profileType, // Assuming 1 = "Search"
      };
      console.log("Fetching profiles for profileType:", profileType);
      dbservice.SendPostRequest(
        "Automata/GetProfiles",
        JSON.stringify(content),
        (response) => {
          console.log("Profiles fetched successfully:", response);
          if (response.profiles && response.profiles.length > 0) {
            const profileOptions = response.profiles.map((profile) => ({
              label: profile.ProfileName,
              value: profile,
            }));
            setProfiles(profileOptions);
          }
        },
        (error) => {
          console.error("Error fetching profiles:", error);
        }
      );
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  const loadProfile = () => {
    if (!selectedProfile) {
      toast.current.show({
        severity: "error",
        summary: t("error"),
        detail: t("profileNotSelected"),
      });
      return;
    }

    const profileDetails = selectedProfile.ProfileDetails;

    const structuredMeasures = profileDetails.reduce((acc, measure) => {
      const machine = profileDetails.Machine;

      // Parent key for machine
      if (!acc[machine]) {
        acc[machine] = { checked: true, partialChecked: false };
      }

      // Child key for measure
      acc[`${measure.Machine}-${measure.SelectedMeasureName}`] = {
        checked: true,
        partialChecked: false,
        id: measure.SelectedMeasureId,
        name: measure.SelectedMeasureName,
        machine: machine,
      };

      return acc;
    }, {});

    setSelectedMeasures(structuredMeasures); // ← set the new structure
    onHide();

    toast.current.show({
      severity: "success",
      summary: t("success"),
      detail: t("profileLoaded"),
    });
  };

  const deleteProfile = () => {
    if (!selectedProfile) {
      toast.current.show({
        severity: "error",
        summary: t("error"),
        detail: t("profileNotSelected"),
      });
      return;
    }
    const body = {
      ProfileId: selectedProfile.ProfileId, // Assuming ProfileId is available in the selected profile
    };
    dbservice.SendPostRequest(
      `Automata/DeleteProfile`,
      JSON.stringify(body),
      (response) => {
        if (response.success) {
          toast.current.show({
            severity: "success",
            summary: t("success"),
            detail: t("profileDeleted", { profileName: selectedProfile.label }),
          });
          fetchProfiles(); // Refresh the profile list
        } else {
          toast.current.show({
            severity: "error",
            summary: t("error"),
            detail: response.message || t("profileDeleteError"),
          });
        }
      },
      (error) => {
        console.error("Error deleting profile:", error);
        toast.current.show({
          severity: "error",
          summary: t("error"),
          detail: t("profileDeleteError"),
        });
      },
      toast.current,
      5000
    );
    setSelectedProfile(null); // Clear the selected profile after deletion
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t("loadProfile")}
      modal
      style={{ width: "30vw", minHeight: "30vh" }}
    >
      <Toast ref={toast} />

      {/* Profile Selection Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <div>
          <label htmlFor="profileSelect">{t("selectProfile")}</label>
          <Dropdown
            id="profileSelect"
            value={selectedProfile}
            options={profiles}
            onChange={(e) => setSelectedProfile(e.value)}
            placeholder={t("selectProfile")}
            style={{ width: "100%", marginTop: "0rem" }}
          />
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "1rem",
            marginTop: "1.2rem",
          }}
        >
          <Button
            label={t("load")}
            icon="pi pi-download"
            onClick={loadProfile}
          />
          <Button
            label={t("delete")}
            icon="pi pi-trash"
            onClick={deleteProfile}
            severity="danger"
          />
        </div>
      </div>

      {/* Profile Details Table */}
      {selectedProfile?.ProfileDetails?.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3>{t("profileDetails")}</h3>
          <DataTable
            value={selectedProfile.ProfileDetails}
            filterDisplay="row"
            filters={filters.current}    
            stripedRows
            paginator
            rows={5}
            selectionMode="single"
            onSelectionChange={(e) => setSelectedProfile(e.value)}
          >
            <Column
              field="SelectedMeasureName"
              header={t("measurename")}
              filter
              sortable
            />
            <Column field="Machine" header={t("machine")} filter sortable />
            {/* Add more columns as needed */}
          </DataTable>
        </div>
      )}
    </Dialog>
  );
}

export default LoadProfilesDialog;
